/**
 * GameHistoryService - Manages game history storage and retrieval
 * Handles saving games to database and fetching game records
 */

import { supabase, isSupabaseConfigured, supabaseRawFetch, getAccessToken } from '../lib/supabase';
import { authService } from '../auth';
import type {
  GameRecord,
  GameWithPlayers,
  GameStats,
  GameHistoryFilter,
  SaveGameData,
  GameType,
  PlayerInfo,
} from './types';
import type { MoveRecord } from '../game/types';

// ========== Helper Functions ==========

/**
 * Transform database row to GameRecord
 */
function rowToGameRecord(row: any): GameRecord {
  return {
    id: row.id,
    player1Id: row.player1_id,
    player2Id: row.player2_id,
    winnerId: row.winner_id,
    boardSize: row.board_size,
    moveHistory: row.move_history as MoveRecord[],
    startedAt: row.started_at,
    endedAt: row.ended_at,
    status: row.status,
    isRanked: row.is_ranked,
    gameType: row.game_type,
  };
}

/**
 * Transform database row with profiles to GameWithPlayers
 */
function rowToGameWithPlayers(row: any): GameWithPlayers {
  const base = rowToGameRecord(row);

  const extractProfile = (profile: any): PlayerInfo | null => {
    if (!profile) return null;
    return {
      id: profile.id,
      username: profile.username || `user_${profile.id?.substring(0, 8)}`,
      displayName: profile.display_name,
      avatarUrl: profile.avatar_url,
    };
  };

  return {
    ...base,
    player1: extractProfile(row.player1),
    player2: extractProfile(row.player2),
    winner: extractProfile(row.winner),
  };
}

// ========== Service Class ==========

export class GameHistoryService {
  /**
   * Save a completed game to the database
   */
  async saveGame(gameData: SaveGameData): Promise<string | null> {
    if (!isSupabaseConfigured()) {
      console.warn('Supabase not configured, game not saved');
      return null;
    }

    try {
      const accessToken = await getAccessToken();
      if (!accessToken) {
        console.warn('Not authenticated, game not saved');
        return null;
      }

      const { data, error } = await supabaseRawFetch<Array<{ id: string }>>('games', {
        method: 'POST',
        body: {
          player1_id: gameData.player1Id,
          player2_id: gameData.player2Id,
          winner_id: gameData.winnerId,
          board_size: gameData.boardSize,
          move_history: gameData.moveHistory,
          started_at: gameData.startedAt?.toISOString() || new Date().toISOString(),
          ended_at: gameData.endedAt?.toISOString() || new Date().toISOString(),
          status: gameData.status || 'completed',
          is_ranked: gameData.isRanked ?? true,
          game_type: gameData.gameType,
        },
        accessToken,
      });

      if (error) {
        console.error('Error saving game:', error);
        return null;
      }

      const gameId = data?.[0]?.id || null;
      console.debug('[GameHistory] Game saved:', gameId);
      return gameId;
    } catch (error) {
      console.error('Error in saveGame:', error);
      return null;
    }
  }

  /**
   * Save a local or AI game (if user is logged in)
   */
  async saveLocalGame(gameData: Omit<SaveGameData, 'player1Id'>): Promise<string | null> {
    const user = authService.getCurrentUser();
    if (!user) {
      console.warn('User not logged in, local game not saved');
      return null;
    }

    return this.saveGame({
      ...gameData,
      player1Id: user.id,
      isRanked: false, // Local/AI games are not ranked
    });
  }

  /**
   * Get paginated game history for the current user
   */
  async getMyGames(
    limit: number = 20,
    offset: number = 0,
    filter?: GameHistoryFilter
  ): Promise<{ games: GameWithPlayers[]; total: number }> {
    const user = authService.getCurrentUser();
    if (!user || !isSupabaseConfigured() || !supabase) {
      return { games: [], total: 0 };
    }

    return this.getGamesForUser(user.id, limit, offset, filter);
  }

  /**
   * Get paginated game history for a specific user
   * Uses parallel fetch for games and profiles (faster than sequential)
   */
  async getGamesForUser(
    userId: string,
    limit: number = 20,
    offset: number = 0,
    filter?: GameHistoryFilter
  ): Promise<{ games: GameWithPlayers[]; total: number }> {
    if (!isSupabaseConfigured()) {
      return { games: [], total: 0 };
    }

    try {
      const accessToken = await getAccessToken();
      
      // Build query string
      let filters = `status=eq.completed&or=(player1_id.eq.${userId},player2_id.eq.${userId})`;
      
      if (filter?.gameType) {
        filters += `&game_type=eq.${filter.gameType}`;
      }
      if (filter?.startDate) {
        filters += `&ended_at=gte.${filter.startDate.toISOString()}`;
      }
      if (filter?.endDate) {
        filters += `&ended_at=lte.${filter.endDate.toISOString()}`;
      }

      const endpoint = `games?${filters}&order=ended_at.desc&offset=${offset}&limit=${limit}`;
      
      const { data, error } = await supabaseRawFetch<Array<any>>(endpoint, {
        accessToken: accessToken || undefined,
        timeoutMs: 8000,
      });

      if (error) {
        console.error('Error fetching games:', error);
        return { games: [], total: 0 };
      }

      const rows = data || [];
      if (rows.length === 0) {
        return { games: [], total: 0 };
      }
      
      // Collect all unique player IDs to fetch profiles
      const playerIds = new Set<string>();
      for (const row of rows) {
        if (row.player1_id) playerIds.add(row.player1_id);
        if (row.player2_id) playerIds.add(row.player2_id);
        if (row.winner_id) playerIds.add(row.winner_id);
      }
      
      // Fetch profiles for all players
      const profiles: Record<string, any> = {};
      if (playerIds.size > 0) {
        const profileQuery = `profiles?id=in.(${Array.from(playerIds).join(',')})&select=id,username,display_name,avatar_url`;
        const { data: profileData } = await supabaseRawFetch<Array<any>>(
          profileQuery,
          { accessToken: accessToken || undefined, timeoutMs: 8000 }
        );
        
        if (profileData) {
          for (const profile of profileData) {
            profiles[profile.id] = profile;
          }
        }
      }
      
      // Helper to extract profile info
      const extractProfile = (id: string | null): PlayerInfo | null => {
        if (!id) return null;
        const profile = profiles[id];
        return {
          id,
          username: profile?.username || `user_${id.substring(0, 8)}`,
          displayName: profile?.display_name || null,
          avatarUrl: profile?.avatar_url || null,
        };
      };

      // Convert to GameWithPlayers with actual profile data
      const games: GameWithPlayers[] = rows.map((row: any) => ({
        ...rowToGameRecord(row),
        player1: extractProfile(row.player1_id),
        player2: extractProfile(row.player2_id),
        winner: extractProfile(row.winner_id),
      }));
      
      return { games, total: games.length };
    } catch (error) {
      console.error('Error in getGamesForUser:', error);
      return { games: [], total: 0 };
    }
  }

  /**
   * Get a single game by ID with full details
   */
  async getGame(gameId: string): Promise<GameWithPlayers | null> {
    if (!isSupabaseConfigured()) {
      return null;
    }

    try {
      const accessToken = await getAccessToken();
      
      // Fetch game
      const { data, error } = await supabaseRawFetch<Array<any>>(
        `games?id=eq.${gameId}&select=*`,
        { accessToken: accessToken || undefined, timeoutMs: 8000 }
      );

      if (error || !data || data.length === 0) {
        console.error('Error fetching game:', error);
        return null;
      }

      const row = data[0];
      
      // Fetch profiles separately if player IDs exist
      const profileIds = [row.player1_id, row.player2_id, row.winner_id].filter(Boolean);
      const profiles: Record<string, any> = {};
      
      if (profileIds.length > 0) {
        const profileQuery = `profiles?id=in.(${profileIds.join(',')})&select=id,username,display_name,avatar_url`;
        const { data: profileData } = await supabaseRawFetch<Array<any>>(
          profileQuery,
          { accessToken: accessToken || undefined, timeoutMs: 8000 }
        );
        
        if (profileData) {
          for (const profile of profileData) {
            profiles[profile.id] = profile;
          }
        }
      }
      
      // Build the GameWithPlayers object
      const extractProfile = (id: string | null): PlayerInfo | null => {
        if (!id) return null;
        const profile = profiles[id];
        return {
          id,
          username: profile?.username || `user_${id.substring(0, 8)}`,
          displayName: profile?.display_name || null,
          avatarUrl: profile?.avatar_url || null,
        };
      };

      return {
        ...rowToGameRecord(row),
        player1: extractProfile(row.player1_id),
        player2: extractProfile(row.player2_id),
        winner: extractProfile(row.winner_id),
      };
    } catch (error) {
      console.error('Error in getGame:', error);
      return null;
    }
  }

  /**
   * Get games played against a specific opponent
   */
  async getGamesWithPlayer(
    opponentId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<{ games: GameWithPlayers[]; total: number }> {
    const user = authService.getCurrentUser();
    if (!user) {
      return { games: [], total: 0 };
    }

    return this.getMyGames(limit, offset, { opponentId });
  }

  /**
   * Get most recent games (any user, completed only)
   */
  async getRecentGames(limit: number = 10): Promise<GameWithPlayers[]> {
    if (!isSupabaseConfigured() || !supabase) {
      return [];
    }

    try {
      const { data, error } = await supabase
        .from('games')
        .select(`
          *,
          player1:profiles!games_player1_id_fkey (id, username, display_name, avatar_url),
          player2:profiles!games_player2_id_fkey (id, username, display_name, avatar_url),
          winner:profiles!games_winner_id_fkey (id, username, display_name, avatar_url)
        `)
        .eq('status', 'completed')
        .eq('game_type', 'online')
        .order('ended_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching recent games:', error);
        return [];
      }

      return (data || []).map(rowToGameWithPlayers);
    } catch (error) {
      console.error('Error in getRecentGames:', error);
      return [];
    }
  }

  /**
   * Delete a game (own games only, non-ranked)
   */
  async deleteGame(gameId: string): Promise<boolean> {
    const user = authService.getCurrentUser();
    if (!user || !isSupabaseConfigured() || !supabase) {
      return false;
    }

    try {
      const { error } = await supabase
        .from('games')
        .delete()
        .eq('id', gameId)
        .or(`player1_id.eq.${user.id},player2_id.eq.${user.id}`)
        .eq('is_ranked', false);

      if (error) {
        console.error('Error deleting game:', error);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error in deleteGame:', error);
      return false;
    }
  }

  /**
   * Get summary statistics for the current user
   */
  async getGameStats(): Promise<GameStats | null> {
    const user = authService.getCurrentUser();
    if (!user || !isSupabaseConfigured() || !supabase) {
      return null;
    }

    try {
      const { data, error } = await supabase
        .from('games')
        .select('winner_id, game_type, move_history')
        .eq('status', 'completed')
        .or(`player1_id.eq.${user.id},player2_id.eq.${user.id}`);

      if (error) {
        console.error('Error fetching game stats:', error);
        return null;
      }

      const games = data || [];
      let wins = 0;
      let losses = 0;
      let draws = 0;
      let onlineGames = 0;
      let aiGames = 0;
      let localGames = 0;
      let totalMoves = 0;

      for (const game of games) {
        // Count results
        if (game.winner_id === null) {
          draws++;
        } else if (game.winner_id === user.id) {
          wins++;
        } else {
          losses++;
        }

        // Count game types
        if (game.game_type === 'online') onlineGames++;
        else if (game.game_type === 'vs_ai') aiGames++;
        else localGames++;

        // Sum moves
        const moves = game.move_history as MoveRecord[];
        totalMoves += moves?.length || 0;
      }

      const totalGames = games.length;
      const winRate = totalGames > 0 ? Math.round((wins / totalGames) * 100) : 0;
      const averageGameLength = totalGames > 0 ? Math.round(totalMoves / totalGames) : 0;

      return {
        totalGames,
        wins,
        losses,
        draws,
        winRate,
        onlineGames,
        aiGames,
        localGames,
        averageGameLength,
      };
    } catch (error) {
      console.error('Error in getGameStats:', error);
      return null;
    }
  }

  /**
   * Generate a shareable link for a game
   */
  generateShareLink(gameId: string): string {
    // Use current origin for the share link
    const origin = typeof window !== 'undefined' ? window.location.origin : '';
    return `${origin}?replay=${gameId}`;
  }

  /**
   * Parse game ID from URL if present
   */
  getReplayIdFromUrl(): string | null {
    if (typeof window === 'undefined') return null;
    const params = new URLSearchParams(window.location.search);
    return params.get('replay');
  }

  /**
   * Determine game result for a specific player
   */
  getResultForPlayer(game: GameRecord, playerId: string): 'win' | 'loss' | 'draw' | null {
    if (game.winnerId === null) {
      return 'draw';
    }
    if (game.winnerId === playerId) {
      return 'win';
    }
    if (game.player1Id === playerId || game.player2Id === playerId) {
      return 'loss';
    }
    return null;
  }
}

// Export singleton instance
export const gameHistoryService = new GameHistoryService();
